import {
  FileUploadConfig,
  isFileUploadValidationError,
  UploadedAttachment,
  validateFiles,
} from "../fileUpload/fileUploadDomain";
import {
  DCEvent,
  EventDispatcher,
  httpDelete,
  httpGet,
  httpPost,
  navigate,
  showError,
  showGeneralError,
  showGeneralSuccess,
  UpdateStoreEvent,
} from "../events/eventsDomain";
import { PendingDecisionListItem } from "../AdminApp/AdminAppController";
import { TabButtonProps } from "../components-v2/Tabs";
import { TMessageKey } from "../i18n/types.gen";
import { BR_STATUS, CompanyItem, DecisionStatus } from "../common/common";
import { DocumentSigningStatus } from "../document/documentApi";
import { compact } from "lodash";
import { isBlank } from "../utils/strings";
import { FilePreviewProps } from "../components-v2/FilePreview";
import { DecisionType } from "../common/decisions";
import { BRSigner } from "../dbot/common/commonDomain";
import { logError } from "../utils/log";
import { createFileTypeValidationErrorMessage } from "../fileUpload/fileUploadErrorMessages";
import { formatDate } from "../i18n/dateFormat";
import { Language } from "../i18n/languages";
import { LocalDateFieldModel, RadioFieldModel, StringFieldModel } from "../common/commonForms";
import { combineDateAndTime, toLocalDateISOString } from "../utils/Dates";
import { EventLog } from "../common/eventLogs";

export const initFollowUpsPage = (decisionId?: string): DCEvent[] =>
  compact([
    { type: "update-store", data: { followUps: defaultFollowUpsState() } },
    followUpsApi.getPendingDecisions({
      onSuccess: (decisions) => [
        setInFollowUpsState("decisions", decisions.pendingDecisions),
        setInFollowUpsState(
          "filterCompaniesModel",
          createFilterCompaniesModel(decisions.pendingDecisions),
        ),
        setInFollowUpsState(
          "filterResponsibleModel",
          createFilterResponsibleModel(decisions.responsibles),
        ),
        setInFollowUpsState("filteredDecisions", decisions.pendingDecisions),
        setInFollowUpsState("responsibles", decisions.responsibles),
        ...selectDecision(decisions.pendingDecisions.find((d) => d.id === decisionId)),
      ],
      onFailure: () => [showGeneralError()],
    }),
  ]);

export type FollowUpTab =
  | "show-all"
  | "new"
  | "deleted"
  | "sent-for-signing"
  | "processing"
  | "approved";

const followUpTabs: { [key in FollowUpTab]: TMessageKey } = {
  "show-all": "followup.tabs.show-all",
  new: "followup.tabs.new",
  deleted: "followup.tabs.deleted",
  "sent-for-signing": "followup.tabs.sent-for-signing",
  processing: "followup.tabs.processing",
  approved: "followup.tabs.approved",
} as const;

export type FollowUpsViewProps = {
  decisions: PendingDecisionProps[];
  tabs: TabButtonProps[];
  activeTab: FollowUpTab;
  onSelectDecision: (decision: PendingDecisionListItem) => void;
  selectedDecision?: DecisionDetailViewProps;
  filterText: string;
  filterCompaniesModel: RadioFieldModel<CompanyItem>;
  filterResponsibleModel: RadioFieldModel<Responsible>;
  updateFilterText: (s: string) => void;
  filterDecisions: () => void;
};

export type PendingDecisionProps = {
  responsibleModel: RadioFieldModel<Responsible>;
} & PendingDecisionListItem;

export type DecisionDetailViewProps = {
  decision: PendingDecisionListItem;
  details?: GetPendingDecisionDetailsResponse;
  previewDocument?: FilePreviewProps;
  emailNotificationText: StringFieldModel;
  brPublishingDateModel: LocalDateFieldModel;
  isRegisteredOnDcompany: boolean;
  assignedToModel: RadioFieldModel<Responsible>;
  onBack: () => void;
  onUpdateStatus: (status: BR_STATUS, brPublishingDate: LocalDateFieldModel) => void;
  onToggleDocumentPreview: (documentId?: string) => void;
  onToggleAttachmentPreview: (id?: string) => void;
  onToggleDecisionPreview: () => void;
  onUploadStatusScreenshot?: (files: File[]) => void;
  onDeleteStatusScreenshot: (attachmentId: string) => void;
  sendUpdateStatusEmailToSubmitter: (email: string) => void;
};

export const unassigned: Responsible = {
  id: "unassigned",
  name: "Unassigned",
  email: "",
};

export type GetPendingDecisionsResponse = {
  pendingDecisions: PendingDecisionListItem[];
  responsibles: Responsible[];
};

export type Responsible = {
  id: string;
  name: string;
  email: string;
};

export type FollowUpDescription = {
  type: string;
  title?: string;
  value?: string;
};

export type GetPendingDecisionDetailsResponse = {
  documents: DocumentMetadata[];
  attachments: UploadedAttachment[];
  companyName: string;
  orgNo: string;
  projectName: string;
  decisionType: DecisionType;
  decisionId: string;
  submittedBy: string;
  brSigners: BRSigner[];
  eventLogs: EventLog[];
  followUpDescriptions: FollowUpDescription[];
  changedData: string;
};

export type DocumentMetadata = {
  documentId: string;
  documentType: DocumentType;
  title: string;
  signingStatus: DocumentSigningStatus;
};

function prepTabs(decisions: PendingDecisionListItem[], activeTab: FollowUpTab): TabButtonProps[] {
  return Object.entries(followUpTabs).map(([id, label]) => ({
    id,
    label: { key: label, params: { count: countForTab(id as FollowUpTab, decisions) } },
    isActive: activeTab === id,
    onSelect: [setInFollowUpsState("activeTab", id as FollowUpTab)],
  }));
}

export const allCompanies: CompanyItem = { organisationNumber: "all", companyName: "" };
export const allResponsibles: Responsible = { id: "all", name: "", email: "" };

function createFilterCompaniesModel(decisions: PendingDecisionListItem[]) {
  const options = decisions.reduce<CompanyItem[]>(
    (acc, { orgNo, companyName }) => {
      if (!acc.some((item) => item.organisationNumber === orgNo)) {
        acc.push({
          organisationNumber: orgNo,
          companyName: companyName,
        });
      }
      return acc;
    },
    [allCompanies],
  );

  return new RadioFieldModel({
    value: allCompanies,
    labelFn: (option) =>
      option.organisationNumber === allCompanies.organisationNumber
        ? { key: "companies-all" }
        : { translated: option.companyName },
    idFn: (option) => option.organisationNumber,
    options,
  });
}

function createFilterResponsibleModel(responsibles: Responsible[]) {
  const options = responsibles.reduce<Responsible[]>(
    (acc, { id, name, email }) => {
      if (!acc.some((r) => r.id === id)) {
        acc.push({ id, name, email });
      }
      return acc;
    },
    [allResponsibles],
  );

  return new RadioFieldModel({
    value: allResponsibles,
    labelFn: ({ id, name }) =>
      id === allResponsibles.id ? { key: "responsibles-all" } : { translated: name },
    idFn: ({ id }) => id,
    options,
  });
}

const prepDecisions = (state: FollowUpsState, dispatch: EventDispatcher) =>
  state.filteredDecisions.map((d) => ({
    ...d,
    responsibleModel: createResponsibleModel(state, d, dispatch),
  }));

export const prepFollowUpsProps = (
  state: FollowUpsState,
  dispatch: EventDispatcher,
): FollowUpsViewProps => {
  const { activeTab, filterText, decisions, filterCompaniesModel, filterResponsibleModel } = state;
  return {
    decisions: prepDecisions(state, dispatch),
    activeTab,
    tabs: prepTabs(decisions, activeTab),
    onSelectDecision: (decision) =>
      dispatch([navigate(`/super-admin-backoffice/followups/${decision.id}`)]),
    selectedDecision: prepSelectedDecisionProps(state, dispatch),
    filterText,
    filterCompaniesModel,
    filterResponsibleModel,
    updateFilterText: (s) =>
      dispatch([
        setInFollowUpsState("filterText", s),
        filterDecisions(state, s, filterCompaniesModel.value, filterResponsibleModel.value),
      ]),
    filterDecisions: () =>
      dispatch([
        filterDecisions(
          state,
          filterText,
          filterCompaniesModel.value,
          filterResponsibleModel.value,
        ),
      ]),
  };
};

export const changeResponsible = (
  decision: PendingDecisionListItem,
  responsible?: Responsible,
): DCEvent[] => [
  followUpsApi.updateResponsible({
    decision,
    responsible,
    onSuccess: () => [showGeneralSuccess()],
    onFailure: () => [showGeneralError()],
  }),
];

const toggleDocumentPreview = (
  details?: GetPendingDecisionDetailsResponse,
  documentId?: string,
): DCEvent[] =>
  compact([
    details &&
      setInFollowUpsState(
        "previewDocument",
        isBlank(documentId)
          ? undefined
          : {
              filename:
                details.documents.find((d) => d.documentId === documentId)!.title +
                `_${details.companyName}_${formatDate(Language.NO, new Date())}.pdf`,
              file: { url: `/api/superadmin/preview-document/${documentId}` },
            },
      ),
  ]);

const uploadStatusScreenshot = (
  dispatch: EventDispatcher,
  decision: PendingDecisionListItem,
  files: File[],
) => {
  const onUpload = (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("file", file));
    dispatch([
      followUpsApi.updateStatusScreenshot({
        decisionId: decision.id,
        formData,
        onSuccess: () => selectDecision(decision),
        onFailure: () => [showGeneralError()],
      }),
    ]);
  };
  if (files.length) {
    validateFiles(files, BRStatusUploadConfig)
      .then(onUpload)
      .catch((err) => {
        if (isFileUploadValidationError(err)) {
          logError(err, "Invalid attachments files");
          dispatch([showError(createFileTypeValidationErrorMessage(err))]);
        }
      });
  }
};

const sendUpdateBRStatusEmail = (
  decision: PendingDecisionListItem,
  emailNotificationText: string,
): DCEvent[] => [
  followUpsApi.sendBRStatusUpdateEmail({
    decisionId: decision.id,
    emailTextMarkdown: emailNotificationText,
    onSuccess: () => [...selectDecision(decision), showGeneralSuccess()],
    onFailure: () => [showGeneralError()],
  }),
];

const deleteStatusScreenshot = (
  decision: PendingDecisionListItem,
  attachmentId: string,
): DCEvent[] => [
  followUpsApi.deleteStatusScreenshot({
    attachmentId,
    onSuccess: () => [...selectDecision(decision), showGeneralSuccess()],
    onFailure: () => [showGeneralError()],
  }),
];

const toggleAttachmentPreview = (
  details?: GetPendingDecisionDetailsResponse,
  attachmentId?: string,
): DCEvent[] =>
  compact([
    details &&
      setInFollowUpsState(
        "previewDocument",
        isBlank(attachmentId)
          ? undefined
          : {
              filename: details.attachments.find((a) => a.id === attachmentId)!.filename,
              file: { url: `/api/superadmin/preview-attachment/${attachmentId}` },
            },
      ),
  ]);

const toggleDecisionPreview = (details?: GetPendingDecisionDetailsResponse): DCEvent[] =>
  compact([
    details &&
      setInFollowUpsState("previewDocument", {
        filename: details.decisionType + ".json",
        file: {
          data: new Blob([new TextEncoder().encode(details.changedData)]),
        },
      }),
  ]);

const showUpdateDecisionStatusDialog = (
  decision: PendingDecisionListItem,
  status: BR_STATUS,
  model: LocalDateFieldModel,
): DCEvent[] => [
  {
    type: "show-dialog",
    dialog: { type: "decision-approval-date", decision, status, model },
  },
];

export const updateDecisionStatus = (
  decision: PendingDecisionListItem,
  status: BR_STATUS,
  brPublishingDate?: string,
  onSuccess?: DCEvent,
): DCEvent[] => [
  followUpsApi.updateDecisionStatus({
    decision,
    status,
    brPublishingDate: brPublishingDate ? combineDateAndTime(brPublishingDate, "12:00") : undefined,
    onSuccess: () =>
      compact([
        onSuccess,
        showGeneralSuccess(),
        updateInFollowUpsState("selectedDecision", (cur) => {
          cur!.status = status;
          if (status === "BR_APPROVED") {
            cur!.brPublishingDate = brPublishingDate ?? toLocalDateISOString(Date.now());
          }
          return cur;
        }),
        updateInFollowUpsState("decisions", (cur) => {
          const d = cur.find((d) => d.id === decision.id);
          d!.status = status;
          return cur;
        }),
      ]),
    onFailure: () => [showGeneralError()],
  }),
];

function createResponsibleModel(
  state: FollowUpsState,
  decision: PendingDecisionListItem,
  dispatch: EventDispatcher,
) {
  return new RadioFieldModel({
    value: state.responsibles.find((r) => r.id === decision.responsibleId) || unassigned,
    labelFn: (option) => ({ translated: option?.name ?? "" }),
    idFn: (option) => option?.id ?? "",
    options: [...state.responsibles, unassigned],
    onChange: (option) =>
      dispatch(changeResponsible(decision, option.id === unassigned.id ? undefined : option)),
  });
}

const prepSelectedDecisionProps = (
  state: FollowUpsState,
  dispatch: EventDispatcher,
): DecisionDetailViewProps | undefined => {
  const decision = state.selectedDecision;
  if (!decision) {
    return undefined;
  }
  return {
    decision,
    details: state.selectedDecisionDetails,
    previewDocument: state.previewDocument,
    isRegisteredOnDcompany: decision.decisionStatus !== DecisionStatus.COMPLETED,
    assignedToModel: createResponsibleModel(state, decision, dispatch),
    onBack: () => dispatch(selectDecision(undefined)),
    onUpdateStatus: (status, dateModel) => {
      if (status === "BR_APPROVED") {
        dispatch(showUpdateDecisionStatusDialog(decision, status, dateModel));
      } else {
        dispatch(updateDecisionStatus(decision, status));
      }
    },
    onToggleDocumentPreview: (documentIdOrEvent) => {
      dispatch(
        toggleDocumentPreview(
          state.selectedDecisionDetails,
          state.previewDocument ? undefined : documentIdOrEvent,
        ),
      );
    },
    onToggleAttachmentPreview: (attachmentIdOrEvent) =>
      dispatch(
        toggleAttachmentPreview(
          state.selectedDecisionDetails,
          state.previewDocument ? undefined : attachmentIdOrEvent,
        ),
      ),
    onToggleDecisionPreview: () => toggleDecisionPreview(state.selectedDecisionDetails),
    onUploadStatusScreenshot:
      decision.status === "PROCESSING"
        ? (files) => uploadStatusScreenshot(dispatch, decision, files)
        : undefined,
    onDeleteStatusScreenshot: (attachmentId: string) =>
      dispatch(deleteStatusScreenshot(decision, attachmentId)),
    emailNotificationText: mapBRStatusToEmailNotificationText(decision),
    brPublishingDateModel: new LocalDateFieldModel({
      value: toLocalDateISOString(decision.brPublishingDate ?? Date.now()),
    }),
    sendUpdateStatusEmailToSubmitter: (email: string) =>
      dispatch(sendUpdateBRStatusEmail(decision, email)),
  };
};

const mapBRStatusToEmailNotificationText = (
  decision: PendingDecisionListItem,
): StringFieldModel => {
  let text = "";
  if (decision.status === "PROCESSING") {
    text = `Meldingen er ferdig signert i Altinn og sendt til Brønnøysundregistrene for behandling.

Tips. Saksbehandlingskøen i Brønnøysundregistrene oppdateres hver time:
https://www.brreg.no/hvordan-kan-vi-hjelpe-deg/nar-blir-saken-min-behandlet/

Mvh oss i dCompany
    `;
  }
  if (decision.status === "BR_APPROVED") {
    text = `Meldingen har blitt godkjent av Brønnøysundregistrene

Mvh oss i dCompany
    `;
  }

  if (decision.status === "SENT_FOR_SIGNING") {
    text = `Samordnet registermelding er opprettet og venter nå på signering i Altinn. Du vil motta en oppdatering så snart meldingen er signert og sendt til Brønnøysundregistrene for behandling.

Mvh. oss i dCompany
    `;
  }
  return new StringFieldModel({ value: text });
};

const selectDecision = (selectedDecision?: PendingDecisionListItem): DCEvent[] =>
  compact([
    setInFollowUpsState("selectedDecision", selectedDecision),
    selectedDecision === undefined && navigate("/super-admin-backoffice/followups"),
    selectedDecision !== undefined &&
      followUpsApi.getPendingDecisionDetails({
        decisionId: selectedDecision!.id,
        onSuccess: (response) => [setInFollowUpsState("selectedDecisionDetails", response)],
        onFailure: () => [showGeneralError()],
      }),
  ]);

export const filterDecisionsList = (
  decisions: PendingDecisionListItem[],
  searchText: string,
  company: CompanyItem,
  responsible: Responsible,
) =>
  decisions.filter((d) => {
    const lowerCased = searchText.toLowerCase();
    const textFilter =
      isBlank(searchText) ||
      d.companyName.toLowerCase().includes(lowerCased) ||
      d.orgNo.includes(lowerCased);

    const companyFilter =
      company.organisationNumber === allCompanies.organisationNumber ||
      company.organisationNumber === d.orgNo;

    const responsibleFilter =
      responsible.id === allResponsibles.id || responsible.id === d.responsibleId;

    return textFilter && companyFilter && responsibleFilter;
  });

const filterDecisions = (
  state: FollowUpsState,
  searchText: string,
  company: CompanyItem,
  responsible: Responsible,
): UpdateStoreEvent =>
  setInFollowUpsState(
    "filteredDecisions",
    filterDecisionsList(state.decisions, searchText, company, responsible),
  );

const countForTab = (tab: FollowUpTab, decisions: PendingDecisionListItem[]): number => {
  switch (tab) {
    case "show-all":
      return decisions.length;
    case "new":
      return decisions.filter((d) => d.status === "SENT_TO_BR").length;
    case "deleted":
      return decisions.filter((d) => d.status === "DELETED").length;
    case "sent-for-signing":
      return decisions.filter((d) => d.status === "SENT_FOR_SIGNING").length;
    case "processing":
      return decisions.filter((d) => d.status === "PROCESSING").length;
    case "approved":
      return decisions.filter((d) => d.status === "BR_APPROVED").length;
  }
};

export const cleanUpFollowUpsPage = (): DCEvent[] => [
  { type: "update-store", data: { followUps: undefined } },
];

export const defaultFollowUpsState = (): FollowUpsState => ({
  decisions: [],
  filteredDecisions: [],
  activeTab: "show-all",
  responsibles: [],
  filterText: "",
  filterCompaniesModel: createFilterCompaniesModel([]),
  filterResponsibleModel: createFilterResponsibleModel([]),
});

export const setInFollowUpsState = <K extends keyof FollowUpsState>(
  key: K,
  val: FollowUpsState[K],
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    followUps: (followUps = defaultFollowUpsState()) => ({ ...followUps, [key]: val }),
  },
});

const updateInFollowUpsState = <K extends keyof FollowUpsState>(
  key: K,
  val: (cur: FollowUpsState[K]) => FollowUpsState[K],
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    followUps: (followUpsState = defaultFollowUpsState()) => ({
      ...followUpsState,
      [key]: val(followUpsState[key]),
    }),
  },
});

export type FollowUpsState = {
  decisions: PendingDecisionListItem[];
  filteredDecisions: PendingDecisionListItem[];
  filterText: string;
  filterCompaniesModel: RadioFieldModel<CompanyItem>;
  filterResponsibleModel: RadioFieldModel<Responsible>;
  responsibles: Responsible[];
  activeTab: FollowUpTab;
  selectedDecision?: PendingDecisionListItem;
  selectedDecisionDetails?: GetPendingDecisionDetailsResponse;
  previewDocument?: FilePreviewProps;
};

const followUpsApi = {
  getPendingDecisions: ({
    onSuccess,
    onFailure,
  }: {
    onSuccess: (pendingDecisionsResponse: GetPendingDecisionsResponse) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `superadmin/pending-decisions`,
      onSuccess: (r) => onSuccess(r.data),
      onFailure,
    }),

  getPendingDecisionDetails: ({
    decisionId,
    onSuccess,
    onFailure,
  }: {
    decisionId: string;
    onSuccess: (response: GetPendingDecisionDetailsResponse) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `superadmin/pending-decision/${decisionId}`,
      onSuccess: (r) => onSuccess(r.data),
      onFailure,
    }),

  updateResponsible: ({
    decision,
    responsible,
    onSuccess,
    onFailure,
  }: {
    decision: PendingDecisionListItem;
    responsible?: Responsible;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/update-responsible`,
      data: { decisionId: decision.id, responsibleId: responsible?.id },
      onSuccess,
      onFailure,
    }),

  updateDecisionStatus: ({
    decision,
    status,
    brPublishingDate,
    onSuccess,
    onFailure,
  }: {
    decision: PendingDecisionListItem;
    status: BR_STATUS;
    brPublishingDate?: string;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/update-decision-status`,
      data: {
        decisionId: decision.id,
        status: status,
        brPublishingDate: status === "BR_APPROVED" ? brPublishingDate : undefined,
      },
      onSuccess,
      onFailure,
    }),

  updateStatusScreenshot: ({
    decisionId,
    formData,
    onSuccess,
    onFailure,
  }: {
    decisionId: string;
    formData: FormData;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/upload-decision-attachment/${decisionId}`,
      data: formData,
      onSuccess,
      onFailure,
    }),

  deleteStatusScreenshot: ({
    attachmentId,
    onSuccess,
    onFailure,
  }: {
    attachmentId: string;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpDelete({
      url: `superadmin/delete-decision-attachment/${attachmentId}`,
      onSuccess,
      onFailure,
    }),

  sendBRStatusUpdateEmail: ({
    decisionId,
    emailTextMarkdown,
    onSuccess,
    onFailure,
  }: {
    decisionId: string;
    emailTextMarkdown: string;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/decision-send-br-status-email`,
      data: { decisionId, emailTextMarkdown },
      onSuccess,
      onFailure,
    }),
};

export const filterPendingDecisionsByTab = (
  decisions: PendingDecisionProps[],
  activeTab: FollowUpTab,
): PendingDecisionProps[] => {
  switch (activeTab) {
    case "show-all":
      return decisions;
    case "new":
      return decisions.filter((d) => d.status === "SENT_TO_BR");
    case "deleted":
      return decisions.filter((d) => d.status === "DELETED");
    case "sent-for-signing":
      return decisions.filter((d) => d.status === "SENT_FOR_SIGNING");
    case "processing":
      return decisions.filter((d) => d.status === "PROCESSING");
    case "approved":
      return decisions.filter((d) => d.status === "BR_APPROVED");
  }
};

export const BRStatusUploadConfig: FileUploadConfig = {
  supportedFileTypes: [{ extensions: ["jpg", "jpeg", "png"], magicBytes: [] }],
  maxFileSize: 5120 * 5120,
};

export const extractBRStatusEmailBody = (event: EventLog): string =>
  JSON.parse(event.eventData).updateText;
